import React, { useMemo, useRef, useState } from "react";
import PitchPlotAudio from "./PitchPlotAudio";
import IntensityPlotAudio from "./IntensityPlotAudio";
import PitchIntensityAnimationUser from "./PitchIntensityAnimationUser";
import MusicNotationView from "./MusicNotationView";
import ColorCodedWordsView from "./ColorCodedWordsView";
import { defaultColormap } from "./colormaps";

const PresentationIntonationFeedbackDisplay = ({
  assessment,
  segment,
  view,
  isAdmin,
  audioUrl,
  narratorAudioUrl,
}) => {
  const [selectedColormap, setSelectedColormap] = useState(
    defaultColormap || "speechStable"
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [showLegend, setShowLegend] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playerSpeed, setPlayerSpeed] = useState(1);
  const [duration, setDuration] = useState(0);

  const recordedAudioRef = useRef(null);

  const audio =
    audioUrl ||
    (assessment?.intonation?.file
      ? `${process.env.REACT_APP_USER_API_URL}/${assessment.intonation.file}`
      : null);

  const togglePlay = async () => {
    const el = recordedAudioRef.current;
    if (!el || !audio) return;

    try {
      if (el.paused) {
        await el.play();
      } else {
        el.pause();
      }
    } catch (err) {
      console.error("Audio play/pause failed:", err);
    }
  };

  const setSpeed = (speed) => {
    setPlayerSpeed(speed);
    if (recordedAudioRef.current) {
      recordedAudioRef.current.playbackRate = speed;
    }
  };

  const progressPct = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return 0;
    return Math.max(0, Math.min(100, (currentTime / duration) * 100));
  }, [currentTime, duration]);

  if (!(assessment?.intonation?.intensity && assessment?.time_stamped)) {
    return (
      <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-gray-500 text-sm">
          Loading user intonation analysis...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      <audio
        ref={recordedAudioRef}
        src={audio || undefined}
        preload="metadata"
        className="hidden"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          setDuration(Number.isFinite(d) ? d : 0);
          e.currentTarget.playbackRate = playerSpeed;
        }}
        onTimeUpdate={(e) => {
          setCurrentTime(e.currentTarget.currentTime);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />

      {/* TOP CONTROLS */}
      {audio && (
        <div className="rounded-2xl border border-emerald-100/80 bg-emerald-50/45 backdrop-blur-sm px-4 py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                disabled={!audio}
                className={[
                  "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all border shadow-sm",
                  audio
                    ? "bg-white text-slate-800 border-emerald-200 hover:bg-emerald-50"
                    : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
                  "h-11 px-4",
                ].join(" ")}
              >
                <span className="text-lg leading-none">
                  {isPlaying ? "⏸" : "▶️"}
                </span>
                <span className="text-sm">
                  {isPlaying ? "Pause" : "Play"} my recording
                </span>
              </button>

              <div className="hidden sm:block text-xs text-slate-500">
                Compare your rise/fall pattern to the narrator
              </div>
            </div>

            <div className="flex items-center gap-3 justify-between md:justify-end">
              <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                Playback speed
              </span>

              <div className="inline-flex rounded-xl bg-white/90 border border-emerald-100 shadow-sm p-1">
                {[0.25, 0.5, 1].map((speed) => {
                  const active = playerSpeed === speed;
                  return (
                    <button
                      key={speed}
                      type="button"
                      onClick={() => setSpeed(speed)}
                      className={[
                        "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                        active
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                          : "text-slate-600 hover:bg-emerald-50",
                      ].join(" ")}
                    >
                      {speed}x
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-white/80 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-green-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* CHART */}
      <div className="rounded-2xl border border-slate-200 bg-white/70 overflow-hidden">
        <div className="p-3">
          {view === "pitch" && (
            <PitchPlotAudio
              audioRef={recordedAudioRef}
              intensity={assessment.intonation.intensity}
              time_stamped={assessment.time_stamped}
              speed={playerSpeed}
            />
          )}

          {view === "intensity" && (
            <IntensityPlotAudio
              audioRef={recordedAudioRef}
              intensity={assessment.intonation.intensity}
              speed={playerSpeed}
            />
          )}

          {view === "combined" && (
            <PitchIntensityAnimationUser
              audioRef={recordedAudioRef}
              intensity={assessment.intonation.intensity.intensity}
              pitch={assessment.intonation.intensity.pitch}
              speed={playerSpeed}
              selectedColormap={selectedColormap}
              onColormapChange={setSelectedColormap}
            />
          )}

          {view === "music" && (
            <MusicNotationView
              pitch={assessment.intonation.intensity.pitch}
              intensity={assessment.intonation.intensity.intensity}
              pitch1stDerivative={
                assessment.intonation.intensity.pitch_1st_derivative
              }
              segment={assessment.time_stamped}
              broadcastTime={currentTime}
            />
          )}

          {view === "colors" && assessment.time_stamped?.words && (
            <ColorCodedWordsView
              words={assessment.time_stamped.words}
              pitch={assessment.intonation.intensity.pitch}
              intensity={assessment.intonation.intensity.intensity}
              segment={assessment.time_stamped}
              broadcastTime={currentTime}
              showLegend={showLegend}
              onToggleLegend={() => setShowLegend(!showLegend)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PresentationIntonationFeedbackDisplay;